import { config } from '@/config'
import { identifyVisitor } from '@/site/lib/analytics'

const OMETRIA_SIGNUP_URL = 'https://api.ometria.com/forms/signup/ajax'
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** The signup form is configured (Ometria form id and the form's account hash). */
export const newsletterEnabled = () => !!config.ometriaFormId && !!config.ometriaFormAccount

export type SignupResult = { ok: true } | { ok: false; error: string }

/**
 * Newsletter signup (Ometria form, sent in the background: "Setting up Ajax forms" in Ometria's help centre), used by
 * the footer form and the popup. Ometria needs its hidden fields exactly as given (form id, account, subscription
 * status, and the two empty honeypot fields that catch bots); the email field is named `ue`. On success the visitor
 * is identified to Ometria's tracker and remembered as subscribed (no more popup).
 */
export async function subscribe(email: string, firstName = ''): Promise<SignupResult> {
  const address = email.trim()
  const name = firstName.trim()
  if (!EMAIL_RE.test(address)) return { ok: false, error: 'Please enter a valid email address.' }

  const body = new URLSearchParams()
  // Ometria's hidden fields, as their form code has them.
  body.set('__form_id', config.ometriaFormId)
  body.set('email', '') // honeypot: must stay empty
  body.set('__email', '') // honeypot: must stay empty
  body.set('@account', config.ometriaFormAccount)
  body.set('@return_url', '')
  body.set('@subscription_status', 'SUBSCRIBED')
  body.set('ue', address)
  if (name) body.set('firstname', name)

  const request = { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' }, body: body.toString() }
  try {
    // Ometria answers with CORS headers (Access-Control-Allow-Origin: *), so its reply is readable: 202 {ok:true}, or
    // 400 {ok:false, errors:[[field, message]]}.
    const rsp = await fetch(OMETRIA_SIGNUP_URL, request)
    const result = (await rsp.json().catch(() => null)) as { ok?: boolean; errors?: [string, string][] } | null
    if (!rsp.ok || result?.ok === false) return { ok: false, error: result?.errors?.[0]?.[1] ?? 'That didn’t work. Please try again in a moment.' }
  } catch {
    return { ok: false, error: 'That didn’t work. Please check your connection and try again.' }
  }
  identifyVisitor(address, name || undefined)
  remember({ subscribed: true })
  return { ok: true }
}

/** The popup waits this long after the page has loaded. */
export const POPUP_DELAY_MS = 3000
/** Once seen (closed or ignored), the popup stays away this long. After a signup it never comes back. */
export const POPUP_QUIET_DAYS = 30
const STORAGE_KEY = 'qt-newsletter'

interface Remembered {
  subscribed?: boolean
  /** When the popup was last shown (ms since epoch). */
  popupShownAt?: number
}

function remembered(): Remembered {
  try {
    return (JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Remembered) ?? {}
  } catch {
    return {}
  }
}

function remember(update: Remembered): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...remembered(), ...update }))
  } catch {
    // Storage blocked (private mode, cookies off): the popup may come back, nothing else breaks.
  }
}

/** Show the popup to this visitor: signup configured, not subscribed, not shown in the last POPUP_QUIET_DAYS. */
export function popupWanted(now: number = Date.now()): boolean {
  if (!newsletterEnabled()) return false
  const { subscribed, popupShownAt } = remembered()
  return !subscribed && !(popupShownAt && now - popupShownAt < POPUP_QUIET_DAYS * 86_400_000)
}

/** Recorded as soon as the popup appears, so it doesn't follow the visitor from page to page. */
export const markPopupShown = (now: number = Date.now()) => remember({ popupShownAt: now })
